import React, {createContext, useReducer} from 'react';
import UploadSchemaReducer, {UploadSchemaActionType} from './UploadSchemaReducer';
import Notification from "../../../../common/Notification";
import * as YamlUtils from "../../../../utils/yaml-utils";
import * as SomlUtils from "../../../../utils/soml-utils";
import {EXISTING_SCHEMA_ID} from "../../../../services/errorcodes";
import SomlService from "../../../../services/soml-service";
import {Schema, SchemaResponse} from "../../../../types/Schema";
import SchemaUploadService from "../../../../services/schema-upload-service";
import {RbacPermissions} from "../../../../types/Rbac";
import {canCreateSchema, noPermissionMessage} from "../../../../utils/permission-utils";
import {Configurations} from "../../../../types/Configurations";

export enum IdValidationState {
  // schema id is missing
  MISSING_ID,
  // schema id is invalid in some way
  WRONG_ID,
  // there is a saved schema with the same id
  DUPLICATED_ID,
  // schema id is valid
  VALID_ID,
  // validation can't be performed for some reason: e.g. if schema reading is forbidden
  CANT_VALIDATE,
  // default
  NONE
}

export type UploadSchemaState = {
  error: any,
  file: any;
  uploadedSchema?: Schema;
  schema?: string;
  schemaYaml?: string;
  schemaId?: string;
  idValidationState: IdValidationState;
  isBound?: boolean;
  saved?: boolean;
  progress: number;
  uploading: boolean;
  loading: boolean;
  permissions?: RbacPermissions;
  isRequestRunning: false,
  // remove an error from the context
  removeError: () => void;
  // show an error notification without touching the context
  showError: (error: any) => void;
  // sets a state about the schema id after its validation
  setIdValidationState: (state: IdValidationState) => void;
  // executes a schema upload in the workbench file store
  uploadSchema: (file: Array<File>) => void;
  // loads a schema from the workbench file store
  getSchema: (filePath: string) => void;
  // save a schema in soaas
  saveSchema: (schema: string) => Promise<boolean>;
  // update an existing schema in soaas
  updateSchema: (schemaId: string, schema: string) => Promise<boolean>;
  // activate existing schema in soaas
  activateSchema: (schemaId: string) => Promise<boolean>;
  // update the schema stored in this context
  changeSchema: (schema: string) => void;
}

const initialState: UploadSchemaState = {
  error: '',
  file: undefined,
  uploadedSchema: undefined,
  schema: undefined,
  schemaYaml: undefined,
  schemaId: undefined,
  idValidationState: IdValidationState.NONE,
  isBound: false,
  saved: false,
  progress: 0,
  uploading: false,
  loading: false,
  permissions: undefined,
  isRequestRunning: false,
  setIdValidationState: (state: IdValidationState) => {
  },
  uploadSchema: (file: Array<File>) => {
  },
  getSchema: (filePath: string) => {
  },
  saveSchema: async (schema: string) => {
    return Promise.resolve(false);
  },
  updateSchema: (schemaId: string, schema: string) => {
    return Promise.resolve(false);
  },
  changeSchema: (schema: string) => {
  },
  activateSchema: (schemaId: string) => {
    return Promise.resolve(false);
  },
  removeError: () => {
  },
  showError: (error) => {
  }
};

export const UploadSchemaContext = createContext(initialState);

type UploadSchemaContextProviderProps = {
  configurations: Configurations;
  children: any;
}

export const UploadSchemaContextProvider = (props: UploadSchemaContextProviderProps) => {
  const [state, dispatch] = useReducer(UploadSchemaReducer, initialState);

  function errorToString(error: any) {
    return typeof error === 'string' ? error : JSON.stringify(error);
  }

  function showErrorNotification(error: any) {
    Notification.error({message: errorToString(error)});
  }

  function resolveError(errorResponse: any) {
    let error = errorResponse;
    if (error.response && error.response.data) {
      error = errorResponse.response.data;
    }
    // it's json-ld payload
    if (error[SomlUtils.SCHEMA_TYPE] === 'Error') {
      error = [{code: error.code, message: error.message}];
    } else if(error[SomlUtils.SCHEMA_HYDRA_MEMBER] && error[SomlUtils.SCHEMA_HYDRA_MEMBER].length) {
      error = error[SomlUtils.SCHEMA_HYDRA_MEMBER].map((err: any) => {
        return {code: err.code, message: err.message};
      })
    } else if (error._embedded && error._embedded.errorResponseList) {
      error = error._embedded.errorResponseList.map((err: any) => {
        return {code: err.code, message: err.message}
      });
    }
    return error;
  }

  const reportError = (errorResponse: any, operation: UploadSchemaActionType, skipNotification = false) => {
    const error = resolveError(errorResponse);
    dispatch({
      type: operation,
      payload: {
        error: error
      }
    });
    !skipNotification && showErrorNotification(error);
  };

  function removeError() {
    dispatch({
      type: UploadSchemaActionType.REMOVE_ERROR,
      payload: {
        error: null
      }
    });
  }

  function showError(error: any) {
    showErrorNotification(resolveError(error))
  }

  const toggleRequestIsRunningFlag = (running: boolean) => {
    dispatch({
      type: UploadSchemaActionType.TOGGLE_REQUEST_IN_PROGRESS,
      payload: {
        isRequestRunning: running
      }
    });
  };

  function setIdValidationState(idValidationState: IdValidationState) {
    dispatch({
      type: UploadSchemaActionType.SET_ID_VALIDATION_STATE,
      payload: {
        idValidationState
      }
    });
  }

  const validateSelectedFile = (content: string) => {
    try {
      if (!content) {
        throw Error('Selected file is empty!');
      }
      YamlUtils.parseYaml(content.toString());
      return true;
    } catch (error) {
      const message = typeof error === 'string' ? error : error.message;
      showErrorNotification(message);
    }
    return false;
  };

  async function uploadSchema(files: Array<File>) {
    // cleanup context
    dispatch({
      type: UploadSchemaActionType.UPLOAD_SCHEMA,
      payload: {
        file: null,
        schema: null,
        schemaYaml: null,
        error: null
      }
    });
    if (files && files.length) {
      // read and do basic validation on selected file
      const reader = new FileReader();
      reader.onload = (evt: any) => {
        const isValid = validateSelectedFile(evt?.target?.result);
        if (isValid) {
          executeUpload(files[0]);
        }
      };
      reader.onerror = () => showErrorNotification(`Can't read selected file!`);
      reader.readAsText(files[0]);
    }
  }

  async function executeUpload(selectedFile: File) {
    try {
      const uploadedSchema = await SchemaUploadService.upload(selectedFile, (progress) => {
        dispatch({
          type: UploadSchemaActionType.UPLOAD_SCHEMA_PROGRESS,
          payload: {
            progress: (progress.loaded / progress.total * 100),
            uploading: true,
            loading: true
          }
        });
      });
      dispatch({
        type: UploadSchemaActionType.SCHEMA_UPLOADED,
        payload: {
          file: uploadedSchema,
          uploading: false,
          loading: false
        }
      });
      Notification.success({message: 'Selected schema was uploaded successfully'});
    } catch (error) {
      reportError(error, UploadSchemaActionType.UPLOAD_SCHEMA_ERROR);
    }
  }

  /**
   * Loads the schema file from the server.
   *
   * @param {string} filePath The path where the file resides on the server.
   * @return {Promise<boolean>}
   */
  async function getSchema(filePath: string) {
    if (!filePath) {
      return 'Schema could not be loaded due to missing file path'
    }
    toggleRequestIsRunningFlag(true);
    dispatch({
      type: UploadSchemaActionType.GET_SCHEMA,
      payload: {
        loading: true
      }
    });
    try {
      const uploadedSchema: Schema = await SchemaUploadService.download(filePath);
      dispatch({
        type: UploadSchemaActionType.SCHEMA_LOADED,
        payload: {
          // TODO: use the uploadedSchema and remove all redundant fields: schema, schemaYaml, id, permissions?
          uploadedSchema,
          schema: uploadedSchema.schema,
          schemaYaml: uploadedSchema.schemaYaml,
          schemaId: uploadedSchema.id,
          loading: false,
          permissions: uploadedSchema.permissions
        }
      });
    } catch (error) {
      reportError(error, UploadSchemaActionType.GET_SCHEMA_ERROR);
      return false;
    } finally {
      toggleRequestIsRunningFlag(false);
    }
    return true;
  }

  const extractSchemaId = () => {
    const yaml: any = state.schema && YamlUtils.parseYaml(state.schema);
    const id = yaml && yaml.id && SomlUtils.resolveSchemaId(yaml.id);
    return id || '';
  };

  const setSchemaId = () => {
    const schemaId = extractSchemaId();
    dispatch({
      type: UploadSchemaActionType.SET_SCHEMA_ID,
      payload: {
        schemaId
      }
    })
  };

  async function saveSchema(schema: string) {
    try {
      removeError();
      // The uploaded schema might have missing id. If that's the case, we try to resolve the id
      // from the schema source if possible.
      setSchemaId();
      if (!canCreateSchema(schema, noPermissionMessage('You do not have permissions to create or update schema with id='))) {
        return false;
      }
      toggleRequestIsRunningFlag(true);
      const response: SchemaResponse = await SomlService.saveSchema(schema);
      let savedSchema = response.schema;
      // Can happen if there are warnings during save
      if (!savedSchema) {
        savedSchema = await SomlService.getSchema(state.schemaId);
      }
      dispatch({
        type: UploadSchemaActionType.SAVE_SCHEMA,
        payload: {
          schemaId: savedSchema.id,
          isBound: savedSchema.bound,
          schema: savedSchema.schema,
          schemaYaml: savedSchema.schemaYaml,
          saved: true,
          permissions: savedSchema.permissions
        }
      });
    } catch (error) {
      let msg = error.message;
      let skipNotification = false;
      if (error.response && error.response.data) {
        msg = error.response.data;
        if (msg.code === EXISTING_SCHEMA_ID) {
          skipNotification = true;
        }
      }
      reportError(error, UploadSchemaActionType.SAVE_SCHEMA_ERROR, skipNotification);
      return false;
    } finally {
      toggleRequestIsRunningFlag(false);
    }
    return true;
  }

  async function updateSchema(schemaId: string, schema: string) {
    try {
      removeError();
      if (!canCreateSchema(schema, noPermissionMessage('You do not have permissions to create or update schema with id='))) {
        return false;
      }
      toggleRequestIsRunningFlag(true);
      const response: SchemaResponse = await SomlService.updateSchema(schemaId, schema);
      let updatedSchema = response.schema;
      // Can happen if there are warnings during save
      if (!updatedSchema) {
        updatedSchema = await SomlService.getSchema(schemaId)
      }
      dispatch({
        type: UploadSchemaActionType.UPDATE_SCHEMA,
        payload: {
          schema: updatedSchema.schema,
          schemaYaml: updatedSchema.schemaYaml,
          isBound: updatedSchema.bound,
          saved: true,
          permissions: updatedSchema.permissions
        }
      });
    } catch (error) {
      reportError(error, UploadSchemaActionType.UPDATE_SCHEMA_ERROR);
      return false;
    } finally {
      toggleRequestIsRunningFlag(false);
    }
    return true;
  }

  function changeSchema(schema: string) {
    dispatch({
      type: UploadSchemaActionType.CHANGE_SCHEMA,
      payload: {
        schema
      }
    });
  }

  async function activateSchema(schemaId: string) {
    try {
      toggleRequestIsRunningFlag(true);
      await SomlService.activateSchema(schemaId, props.configurations);
    } catch (error) {
      reportError(error, UploadSchemaActionType.ACTIVATE_SCHEMA_ERROR);
      return false;
    } finally {
      toggleRequestIsRunningFlag(false);
    }
    return true;
  }

  return (
    <UploadSchemaContext.Provider value={{
      error: state.error,
      file: state.file,
      uploadedSchema: state.uploadedSchema,
      schema: state.schema,
      schemaYaml: state.schemaYaml,
      schemaId: state.schemaId,
      permissions: state.permissions,
      isRequestRunning: state.isRequestRunning,
      idValidationState: state.idValidationState,
      progress: state.progress,
      isBound: state.isBound,
      saved: state.saved,
      uploading: state.uploading,
      loading: state.loading,
      uploadSchema,
      setIdValidationState,
      getSchema,
      saveSchema,
      updateSchema,
      changeSchema,
      activateSchema,
      removeError,
      showError
    }}>
      {props.children}
    </UploadSchemaContext.Provider>
  );
};
